import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { BarChart as TremorBarChart } from "@tremor/react";
import type { BestTimeDataPoint } from "./instagram.types";

interface InstagramBestTimesProps {
  bestDayData: BestTimeDataPoint[];
  bestHourData: BestTimeDataPoint[];
  loading?: boolean;
}

/** Skeleton for the best times section */
function BestTimesSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {[...Array(2)].map((_, i) => (
        <Card key={`bt-sk-${i}`}>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-52 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function InstagramBestTimes({
  bestDayData,
  bestHourData,
  loading = false,
}: InstagramBestTimesProps): React.ReactElement {
  if (loading) return <BestTimesSkeleton />;

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Best day */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              Melhor dia para postagens
              <HelpTooltip
                content="Dias da semana com maior audiência online"
                triggerClassName="h-3.5 w-3.5 text-[9px]"
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TremorBarChart
            className="h-52"
            data={bestDayData.map((point) => ({
              ...point,
              valor: point.value,
            }))}
            index="label"
            categories={["valor"]}
            colors={["emerald"]}
            valueFormatter={(v) => v.toLocaleString("pt-BR")}
            showLegend={true}
            showGridLines={true}
            showAnimation={true}
          />
          <p className="text-[10px] text-muted-foreground mt-1 text-center">
            Seguidores Online
          </p>
        </CardContent>
      </Card>

      {/* Best hour */}
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              Melhor horário para postagens
              <HelpTooltip
                content="Horários com mais seguidores online"
                triggerClassName="h-3.5 w-3.5 text-[9px]"
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TremorBarChart
            className="h-52"
            data={bestHourData.map((point) => ({
              ...point,
              valor: point.value,
            }))}
            index="label"
            categories={["valor"]}
            colors={["emerald"]}
            valueFormatter={(v) => v.toLocaleString("pt-BR")}
            showLegend={true}
            showGridLines={true}
            showAnimation={true}
          />
          <p className="text-[10px] text-muted-foreground mt-1 text-center">
            Seguidores Online
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
